import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowRight,
  faBolt,
  faBookOpen,
  faChevronRight,
  faCircleCheck,
  faClock,
  faCreditCard,
  faDownload,
  faGift,
  faLayerGroup,
  faLock,
  faShieldHalved,
  faStar,
  faStore,
  faTag,
  faVideo,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { getStorefrontConfig } from '../lib/dropify'
import { whatsAppCtaProps, track } from '../lib/analytics'
import type { FaqItem } from '../lib/seo'
import {
  absoluteUrl,
  brandTitle,
  buildBreadcrumbSchema,
  buildFaqSchema,
  SITE_NAME,
} from '../lib/seo'

// Ebook sales go to a dedicated WhatsApp number, separate from the Dropify
// grosir admin line (+62 811-3143-975).
const WA_NUMBER = '628113143975'
const LEAD_SOURCE = 'ebook' as const

const EBOOK_TITLE = 'Membangun Toko Online Sendiri dengan WordPress'
const EBOOK_PRICE = 150000
const EBOOK_PRICE_LABEL = 'Rp 150.000'
const COVER_IMAGE = '/ebook/cover.webp'
const PATH = '/ebook-bikin-toko-online'

const SEO_TITLE = 'Ebook Cara Bikin Toko Online Sendiri dengan WordPress'
const SEO_DESCRIPTION =
  'Panduan PDF untuk menyiapkan toko online dengan WordPress dan WooCommerce, mulai dari domain dan hosting hingga katalog, pembayaran, dan ongkir. Harga Rp 150.000 termasuk video tutorial.'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

const ORDER_MESSAGE = `Halo Dropify, saya mau pesan Ebook "${EBOOK_TITLE}" (${EBOOK_PRICE_LABEL}). Mohon info cara pesan dan pembayarannya.`

const EBOOK_FAQ: FaqItem[] = [
  {
    q: 'Apa yang didapat dengan harga Rp 150.000?',
    a: 'Kamu mendapat ebook dalam format PDF dan video tutorial. Materinya membahas domain, hosting, WordPress, WooCommerce, katalog, pembayaran, dan ongkir.',
  },
  {
    q: 'Apakah panduan ini bisa diikuti tanpa pengalaman coding?',
    a: 'Bisa. Langkahnya dimulai dari pembelian domain dan hosting, lalu pemasangan WordPress serta WooCommerce hingga toko dapat menerima pesanan.',
  },
  {
    q: 'Bagaimana cara memesan dan membayar?',
    a: 'Klik tombol pemesanan untuk menghubungi admin melalui WhatsApp. Admin akan menjelaskan pembayaran dan mengirim akses setelah pembayaran dikonfirmasi.',
  },
  {
    q: 'Apakah ebook ini sesuai untuk reseller?',
    a: 'Ya. Materinya mencakup pemindahan katalog ke website, pengaturan kanal penjualan, dan pengelolaan data pesanan yang masuk melalui toko sendiri.',
    link: { href: '/cara-jadi-reseller', label: 'Pelajari cara jadi reseller' },
  },
  {
    q: 'Ada biaya lain di luar Rp 150.000?',
    a: 'Biaya ebook belum mencakup domain dan hosting. Keduanya dibayar kepada penyedia yang kamu pilih. Ebook memuat contoh pilihan layanan dan komponen biaya yang perlu dihitung.',
  },
]

const EBOOK_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: EBOOK_TITLE,
  description: SEO_DESCRIPTION,
  image: [absoluteUrl(COVER_IMAGE)],
  brand: { '@type': 'Brand', name: SITE_NAME },
  category: 'Ebook Panduan',
  offers: {
    '@type': 'Offer',
    priceCurrency: 'IDR',
    price: EBOOK_PRICE,
    availability: 'https://schema.org/InStock',
    url: absoluteUrl(PATH),
    seller: { '@type': 'Organization', name: SITE_NAME },
  },
}

export const Route = createFileRoute('/ebook-bikin-toko-online')({
  loader: async () => ({ config: await getStorefrontConfig().catch(() => null) }),
  head: () => {
    const title = brandTitle(SEO_TITLE)
    const image = absoluteUrl(COVER_IMAGE)

    return {
      meta: [
        { title },
        { name: 'description', content: SEO_DESCRIPTION },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:type', content: 'product' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: SEO_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl(PATH) },
        { property: 'og:image', content: image },
        { property: 'product:price:amount', content: String(EBOOK_PRICE) },
        { property: 'product:price:currency', content: 'IDR' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: SEO_DESCRIPTION },
        { name: 'twitter:image', content: image },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(EBOOK_JSON_LD),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(EBOOK_FAQ)),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Ebook Toko Online', path: PATH },
            ]),
          ),
        },
      ],
    }
  },
  component: EbookPage,
})

type Bundle = {
  icon: IconDefinition
  title: string
  body: string
  tag: string
  bonus?: boolean
}

const BUNDLE: Bundle[] = [
  {
    icon: faBookOpen,
    tag: 'Yang kamu bayar',
    title: 'Ebook panduan lengkap',
    body: 'Setiap langkah dilengkapi penjelasan dan gambar, mulai dari konfigurasi awal hingga toko dapat menerima pesanan.',
  },
  {
    icon: faVideo,
    tag: 'Video tutorial',
    title: 'Ikuti langkah konfigurasi',
    body: 'Video memperlihatkan bagian konfigurasi teknis yang dibahas di dalam ebook.',
    bonus: true,
  },
]

type Chapter = { icon: IconDefinition; title: string; body: string }

const CHAPTERS: Chapter[] = [
  {
    icon: faStore,
    title: 'Instalasi & pengaturan awal',
    body: 'Pasang WordPress dan WooCommerce dari nol. Sampai setelan dasar toko yang wajib kamu cek dulu sebelum mulai jualan.',
  },
  {
    icon: faLayerGroup,
    title: 'Produk & katalog',
    body: 'Bikin produk dari yang simpel sampai yang banyak variasi. Rapiin kategori, samain gaya foto, plus bikin halaman produk yang meyakinkan.',
  },
  {
    icon: faBookOpen,
    title: 'Tampilan toko',
    body: 'Pilih theme, pasang logo dan identitas toko. Atur tampilan beranda, menu, keranjang, dan halaman penting lainnya.',
  },
  {
    icon: faCreditCard,
    title: 'Pembayaran & ongkir otomatis',
    body: 'Pasang Duitku buat terima pembayaran. Atur ongkir dan zona kirim. Plus alur ngurus pesanan yang masuk tiap hari.',
  },
]

const AUDIENCE = [
  'Reseller yang ingin menambah website sebagai kanal penjualan',
  'Penjual online yang ingin mengelola katalog dan data pesanan melalui website sendiri',
  'Pemula yang belum pernah membuat website atau menulis kode',
  'Pemilik usaha yang ingin memakai domain sendiri',
]

type Pain = { icon: IconDefinition; title: string; body: string }

const PAINS: Pain[] = [
  {
    icon: faStore,
    title: 'Aturan mengikuti marketplace',
    body: 'Katalog dan akun di marketplace mengikuti kebijakan platform yang digunakan.',
  },
  {
    icon: faTag,
    title: 'Harga saling banting',
    body: 'Produk serupa tampil berdampingan sehingga harga menjadi salah satu pembanding utama bagi calon pembeli.',
  },
  {
    icon: faLock,
    title: 'Data mengikuti aturan platform',
    body: 'Akses ke data pembeli dibatasi oleh kebijakan marketplace. Website sendiri memberi kanal terpisah untuk mengelola data pesanan yang masuk.',
  },
]

const REASSURANCE = [
  'Langkah dimulai dari konfigurasi awal dan dilengkapi gambar',
  'Video tutorial memperlihatkan proses konfigurasi',
  'Akses ebook dan video dikirim setelah pembayaran dikonfirmasi',
]

function CtaButton({
  className = '',
  label = 'Pesan ebook via WhatsApp',
  placement,
  big = false,
}: {
  className?: string
  label?: string
  placement: string
  big?: boolean
}) {
  return (
    <div className={`relative inline-flex ${big ? 'w-full sm:w-auto' : ''}`}>
      <span
        className="pointer-events-none absolute -inset-1 rounded-2xl bg-green-500/40 blur-lg"
        aria-hidden
      />
      <a
        href={waLink(ORDER_MESSAGE)}
        {...whatsAppCtaProps(LEAD_SOURCE, { lead_item_name: EBOOK_TITLE, cta_placement: placement })}
        target="_blank"
        rel="noopener noreferrer"
        className={`relative inline-flex w-full items-center justify-center gap-2.5 rounded-2xl bg-green-700 font-bold text-white shadow-lg shadow-green-600/30 transition-all hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-xl hover:shadow-green-600/40 ${
          big ? 'px-8 py-4 text-base md:text-lg' : 'px-6 py-3.5 text-sm'
        } ${className}`}
      >
        <FontAwesomeIcon icon={faWhatsapp} className={big ? 'h-6 w-6' : 'h-5 w-5'} />
        {label}
      </a>
    </div>
  )
}

function PriceTag({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-baseline gap-2 whitespace-nowrap ${className}`}>
      <span className="text-2xl font-black tracking-tight md:text-3xl">{EBOOK_PRICE_LABEL}</span>
      <span className="text-xs font-medium opacity-70">sekali bayar</span>
    </div>
  )
}

function EbookPage() {
  const { config } = Route.useLoaderData()

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden bg-slate-900">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          aria-hidden
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        <div className="pointer-events-none absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-500/25 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-32 -right-16 h-96 w-96 rounded-full bg-indigo-500/25 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/10 blur-3xl" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-4 py-14 md:py-20">
          <nav className="mb-8 flex items-center gap-1.5 text-xs text-slate-400">
            <a href="/" className="transition-colors hover:text-white">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
            <span className="font-medium text-white">Ebook Toko Online</span>
          </nav>

          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-widest text-amber-300">
                <FontAwesomeIcon icon={faBolt} className="h-3 w-3" />
                Ebook + Video Tutorial
              </span>
              <h1 className="mt-6 text-[2.75rem] font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Bangun{' '}
                <span className="relative whitespace-nowrap bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
                  toko online sendiri
                </span>
              </h1>

              <div className="mt-7 flex flex-col gap-2.5">
                {[
                  { text: 'Ebook panduan lengkap, langkah demi langkah' },
                  { text: 'Video tutorial biar bisa lihat praktiknya', bonus: true },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-2.5 text-sm text-slate-200 md:text-base">
                    <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                    <span>
                      {item.text}
                      {item.bonus && (
                        <span className="ml-2 inline-flex items-center rounded-md bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-amber-300">
                          Gratis
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-5 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
                <PriceTag className="text-white" />
                <CtaButton placement="hero" label="Pesan via WhatsApp" big />
              </div>
              <button
                type="button"
                data-preview-download
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3.5 text-sm font-bold text-slate-200 transition-colors hover:bg-white/10"
              >
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                Unduh cuplikan ebook
              </button>
              <p className="mt-3 text-sm text-slate-400">
                Pemesanan dan pembayaran dilakukan melalui WhatsApp. Admin mengirim akses setelah pembayaran dikonfirmasi.
              </p>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-8 rounded-[2rem] bg-gradient-to-tr from-amber-400/20 via-blue-500/20 to-indigo-400/10 blur-3xl" aria-hidden />
                <img
                  src={COVER_IMAGE}
                  alt={`Sampul ebook ${EBOOK_TITLE}`}
                  width="1000"
                  height="1414"
                  className="relative w-60 rotate-[-3deg] rounded-xl shadow-2xl ring-1 ring-white/10 transition-transform duration-300 hover:rotate-0 md:w-80"
                />
                <div className="absolute -bottom-4 -left-4 rotate-[-6deg] rounded-2xl border border-amber-300/40 bg-slate-900/90 px-4 py-3 shadow-xl backdrop-blur-sm">
                  <p className="text-2xl font-black leading-none text-amber-300">A-Z</p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-300">Lengkap</p>
                </div>
                <div className="absolute -top-4 -right-3 rotate-[6deg] rounded-2xl border border-white/15 bg-white px-4 py-3 shadow-xl">
                  <p className="text-2xl font-black leading-none text-slate-900">A-Z</p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Panduan setup</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WhatsAppChannelSection />

      <div className="mx-auto max-w-7xl px-4">
        {/* Pain */}
        <section className="mt-14 md:mt-16">
          <div className="mb-8 max-w-2xl">
            <p className="text-xs font-black uppercase tracking-widest text-rose-500">Masalahnya</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Saat kamu membutuhkan toko online milik sendiri
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-base">
              Marketplace menetapkan aturan akun, tampilan katalog, dan akses data pembeli.
              Website sendiri memberi kamu kanal tambahan untuk mengelola katalog serta pesanan.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {PAINS.map((pain) => (
              <div key={pain.title} className="relative rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                  <FontAwesomeIcon icon={pain.icon} className="h-5 w-5" />
                </div>
                <div className="absolute right-5 top-6 flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-rose-400">
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </div>
                <h3 className="mt-4 text-lg font-black text-slate-900">{pain.title}</h3>
                <p className="mt-1.5 text-base leading-relaxed text-slate-600">{pain.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Promise / value stack */}
        <section className="mt-14 md:mt-16">
          <div className="mb-8 text-center">
            <p className="text-xs font-black uppercase tracking-widest text-blue-600">Isi paket pembelajaran</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Ebook dan video tutorial
            </h2>
          </div>
          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-5 md:grid-cols-2">
            {BUNDLE.map((item) => (
              <div
                key={item.tag}
                className={`relative overflow-hidden rounded-2xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${
                  item.bonus
                    ? 'border-amber-200 ring-1 ring-amber-100'
                    : 'border-blue-200 ring-2 ring-blue-100'
                }`}
              >
                {item.bonus ? (
                  <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-sm">
                    <FontAwesomeIcon icon={faGift} className="h-3 w-3" />
                    Termasuk dalam harga
                  </span>
                ) : (
                  <span className="absolute right-4 top-4 inline-flex items-center rounded-full bg-blue-600 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-sm">
                    Ebook
                  </span>
                )}
                <div className="relative">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-lg ${
                      item.bonus ? 'bg-amber-500 shadow-amber-500/20' : 'bg-blue-600 shadow-blue-600/20'
                    }`}
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <span
                    className={`mt-4 inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-bold ${
                      item.bonus ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    {item.tag}
                  </span>
                  <h3 className="mt-3 text-lg font-black text-slate-900">{item.title}</h3>
                  <p className="mt-1.5 text-base leading-relaxed text-slate-600">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What's inside */}
        <section className="mt-14 md:mt-16">
          <div className="mb-8 text-center">
            <p className="text-xs font-black uppercase tracking-widest text-blue-600">Isi ebook</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Materi disusun dari konfigurasi awal
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500 md:text-base">
              Bab disusun berurutan agar kamu dapat mengikuti proses dari instalasi hingga pengelolaan pesanan.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CHAPTERS.map((chapter, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-md"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white transition-colors group-hover:bg-blue-600">
                    <FontAwesomeIcon icon={chapter.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-black text-slate-300">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="mt-4 text-sm font-black text-slate-900">{chapter.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{chapter.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Audience */}
        <section className="mt-14 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm md:mt-16 md:p-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-blue-600">Untuk siapa</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
                Siapa yang dapat memakai panduan ini?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-base">
                Panduan dimulai dari dasar dan dapat diikuti tanpa pengalaman coding. Laptop atau PC
                disarankan untuk konfigurasi awal.
              </p>
            </div>
            <ul className="flex flex-col gap-3">
              {AUDIENCE.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3.5 text-sm text-slate-700">
                  <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Reassurance / risk reversal */}
        <section className="mt-14 rounded-3xl border border-green-100 bg-green-50/60 p-8 md:mt-16 md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-center">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-green-700 text-white shadow-lg shadow-green-600/30">
              <FontAwesomeIcon icon={faShieldHalved} className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
                Panduannya lengkap sampai tokomu jadi
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {REASSURANCE.map((item) => (
                  <div key={item} className="flex items-start gap-2.5 rounded-xl bg-white px-4 py-3.5 text-sm text-slate-700 shadow-sm">
                    <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Offer / price recap */}
        <section className="relative mt-14 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 p-8 text-center shadow-2xl md:mt-16 md:p-14">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            aria-hidden
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
          />
          <div className="pointer-events-none absolute -top-16 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/15 blur-3xl" aria-hidden />
          <div className="relative mx-auto max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-widest text-amber-300">
              <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
              Isi paket
            </span>
            <h2 className="mt-5 text-3xl font-black leading-tight tracking-tight text-white md:text-4xl">
              Siapkan toko online dengan panduan terstruktur
            </h2>

            <div className="mx-auto mt-7 flex max-w-md flex-col gap-2.5 text-left">
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <FontAwesomeIcon icon={faBookOpen} className="h-4 w-4 flex-shrink-0 text-blue-300" />
                <span className="flex-1 text-sm font-bold text-white">Ebook panduan lengkap</span>
                <span className="text-sm font-black text-white">{EBOOK_PRICE_LABEL}</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
                <FontAwesomeIcon icon={faVideo} className="h-4 w-4 flex-shrink-0 text-amber-300" />
                <span className="flex-1 text-sm font-bold text-amber-100">Video tutorial konfigurasi</span>
                <span className="text-sm font-black text-amber-300">Termasuk</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col items-center gap-5">
              <div className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-6 py-4 backdrop-blur-sm">
                <FontAwesomeIcon icon={faClock} className="h-6 w-6 text-amber-300" />
                <PriceTag className="text-white" />
              </div>
              <CtaButton placement="offer" label="Pesan ebook via WhatsApp" big className="max-w-md" />
              <button
                type="button"
                data-preview-download
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-slate-200 transition-colors hover:bg-white/10"
              >
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                Unduh cuplikan ebook
              </button>
              <p className="text-xs text-slate-400">Pesannya lewat WhatsApp admin Dropify.</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14 mb-16 md:mt-16">
          <div className="mb-8 text-center">
            <p className="text-xs font-black uppercase tracking-widest text-blue-600">Pertanyaan umum</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Informasi sebelum membeli ebook
            </h2>
          </div>
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            {EBOOK_FAQ.map((item) => (
              <details key={item.q} className="group rounded-2xl border border-slate-100 bg-white p-5 open:shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-bold text-slate-900 md:text-base">
                  {item.q}
                  <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-base leading-relaxed text-slate-600">{item.a}</p>
                {item.link && (
                  <a
                    href={item.link.href}
                    onClick={() =>
                      track('click_internal_link', {
                        link_url: item.link!.href,
                        link_text: item.link!.label,
                        link_location: 'ebook_faq',
                      })
                    }
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 transition-colors hover:text-blue-700"
                  >
                    {item.link.label}
                    <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
                  </a>
                )}
              </details>
            ))}
          </div>

          <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center gap-4 rounded-2xl bg-slate-900 p-8 text-center">
            <p className="text-lg font-black text-white md:text-xl">Pesan ebook dan terima akses melalui WhatsApp</p>
            <CtaButton placement="faq" label="Pesan ebook via WhatsApp" big className="max-w-md" />
          </div>
        </section>
      </div>

      <Footer config={config} />
    </div>
  )
}
